import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MemberIdTypeClient, MemberIdTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberIdTypeClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const useMemberIdTypes = () => {
  return useQuery({
    queryKey: ['memberIdTypes'],
    queryFn: async () => {
      const response = await apiClient.searchMemberIdTypes({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Uncached search for admin tables
export const useMemberIdTypeSearch = () => {
  const search = async (params: any) => {
    const response = await apiClient.searchMemberIdTypes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query by ID
export const useMemberIdTypeById = (id: string) => {
  return useQuery({
    queryKey: ['memberIdType', id],
    queryFn: async () => {
      const response = await apiClient.getMemberIdTypeById(id);
      return response?.data || null;
    },
    enabled: !!id,
  });
};

// Create mutation
export const useCreateMemberIdType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: MemberIdTypeDTO) => {
      return await apiClient.createMemberIdType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberIdTypes'] });
    },
  });
};

// Update mutation
export const useUpdateMemberIdType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: MemberIdTypeDTO }) => {
      return await apiClient.updateMemberIdType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberIdTypes'] });
    },
  });
};

// Delete mutation
export const useDeleteMemberIdType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteMemberIdType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberIdTypes'] });
    },
  });
};